import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Navbar from "../components/site/Navbar";
import ContactRail from "../components/site/ContactRail";
import { Footer } from "../components/site/Sections5";
import PageHero from "../components/site/PageHero";
import DedicatedCTA from "../components/site/DedicatedCTA";
import SEO from "../components/site/SEO";
import { INDUSTRIES } from "../constants/industriesData";

export default function IndustriesOverview() {
  return (
    <div className="bg-white min-h-screen flex flex-col" data-testid="industries-overview-page">
      <SEO
        title="Industries Served | Specialized Sector Marketing"
        description="Discover ADMALL INDIA's specialized marketing, production, and lead generation frameworks across Real Estate, Manufacturing & B2B, D2C, Healthcare, Education, Hospitality, and Entertainment."
        canonical="https://admallindia.com/industries"
      />
      <Navbar logo="/admall-logo.png" />
      <ContactRail />

      <main className="flex-grow">
        <PageHero
          category="Sectors"
          categoryLink="/industries"
          title="Built For Businesses That Want To Move"
          tagline="Generic marketing formulas fail across specialized sectors. We build tailored visual production and performance growth systems calibrated for industry-specific sales dynamics."
          bgImage="https://images.unsplash.com/photo-1723110994499-df46435aa4b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          ctaLabel="Consult On Your Sector"
        />

        {/* Sector Strategy Framing */}
        <section className="bg-white py-16 md:py-24 border-b border-navy/10">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="max-w-3xl">
              <div className="mb-4 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
                <span>Sector Intelligence</span>
              </div>
              <h2 className="font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-navy sm:text-5xl">
                Every sector has its own customer psychology and buying cycle.
              </h2>
              <p className="mt-6 font-body text-base leading-relaxed text-navy/70 sm:text-lg">
                A homebuyer does not make decisions the way an industrial procurement director does; an institutional admission cycle follows an entirely different timeline than a weekend hospitality booking. We bring deep sector-specific experience, understanding the exact balance of creative emotion and commercial logic each industry demands.
              </p>
            </div>
          </div>
        </section>

        {/* Industries Grid */}
        <section className="bg-[#F8F9FA] py-20 md:py-28">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {INDUSTRIES.map((ind) => (
                <article
                  key={ind.slug}
                  data-testid={`industry-card-${ind.slug}`}
                  className="group flex flex-col justify-between overflow-hidden rounded-3xl border border-navy/10 bg-white transition hover:border-[#FF5722] hover:shadow-xl"
                >
                  <div>
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={ind.heroImage}
                        alt={ind.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/20 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5722] px-3 py-1 font-body text-xs font-bold uppercase tracking-wide text-white">
                          Sector Vertical
                        </span>
                      </div>
                    </div>

                    <div className="p-7">
                      <h3 className="font-display text-2xl font-black uppercase tracking-tight text-navy group-hover:text-[#FF5722] transition">
                        {ind.title}
                      </h3>
                      <p className="mt-2 font-display text-sm font-bold uppercase tracking-wide text-[#FF5722]">
                        {ind.tagline}
                      </p>
                      <p className="mt-4 font-body text-sm leading-relaxed text-navy/70 line-clamp-3">
                        {ind.summary}
                      </p>
                    </div>
                  </div>

                  <div className="p-7 pt-0">
                    <div className="border-t border-navy/10 pt-5 flex items-center justify-between">
                      <Link
                        to={`/industries/${ind.slug}`}
                        className="inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-[#FF5722] group-hover:translate-x-1 transition"
                      >
                        Explore Industry Strategy <ArrowRight size={15} />
                      </Link>
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-navy/5 text-navy group-hover:bg-[#FF5722] group-hover:text-white transition">
                        <ArrowUpRight size={16} />
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <DedicatedCTA
          title="Don't see your specific niche?"
          subtitle="Our integrated advertising, multimedia, and performance principles translate across emerging and specialized sectors. Connect with us to explore a tailored engagement."
        />
      </main>

      <Footer
        content={{
          logo_url: "/admall-logo.png",
          footer: {
            tagline: "THINK. CREATE. REACH. PERFORM. GROW.",
            disciplines: "Brand Strategy | Creative | Multimedia | Digital Marketing | Advertising | Lead Generation | AI Marketing",
          },
          settings: {
            phone: "+91 93221 06632",
            phone_raw: "+919322106632",
            email: "admallindiagroup@gmail.com",
            location: "Navi Mumbai, Maharashtra, India",
            website: "admallindia.com",
          },
        }}
      />
    </div>
  );
}
